import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, TagField, TextInput } from '@yoast/ui-library';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import {
  getGetAnalysisQueryKey,
  getGetAnalysisStatusQueryKey,
  getGetAssociatedBrandsQueryKey,
  useCreateBrandAssociationGroup,
  useDeleteBrandAssociationGroup,
} from '../../api/generated/insights';
import type {
  AssociatedBrandsResponseAssociatedBrandsItem,
  GroupedAssociatedBrand,
  GroupedAssociatedBrandType,
  UngroupedAssociatedBrand,
  UngroupedAssociatedBrandType,
} from '../../api/generated/insights.schemas';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { useBrandAnalysisData } from '../../contexts';

type BrandOrGroup = AssociatedBrandsResponseAssociatedBrandsItem extends {
  type: GroupedAssociatedBrandType;
}
  ? GroupedAssociatedBrand
  : AssociatedBrandsResponseAssociatedBrandsItem extends {
        type: UngroupedAssociatedBrandType;
      }
    ? UngroupedAssociatedBrand
    : AssociatedBrandsResponseAssociatedBrandsItem;

interface EditGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupToEdit: BrandOrGroup | null;
  allBrands: BrandOrGroup[];
}

const EditGroupModal = ({
  isOpen,
  onClose,
  groupToEdit,
  allBrands,
}: EditGroupModalProps) => {
  const { brandId, analysisId } = useBrandAnalysisData();
  const [groupName, setGroupName] = useState('');
  const [groupedBrandIds, setGroupedBrandIds] = useState<string[]>([]);
  const [isUngroupConfirmOpen, setIsUngroupConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  // Initialize state when modal opens or group changes
  useEffect(() => {
    if (isOpen && groupToEdit && groupToEdit.type === 'group') {
      setGroupName(String(groupToEdit.name || ''));

      // Extract grouped brand IDs from the group
      const brandIds =
        groupToEdit.groupedBrands?.map((brand) => String(brand.id || '')) || [];
      setGroupedBrandIds(brandIds);
    }
  }, [isOpen, groupToEdit]);

  const { mutate: createGroup, isPending: isPendingCreateGroup } =
    useCreateBrandAssociationGroup({
      mutation: {
        onSuccess: async () => {
          await Promise.all([
            // Invalidate associated brands query to refresh the brand list
            queryClient.invalidateQueries({
              queryKey: getGetAssociatedBrandsQueryKey(brandId),
            }),
            // Grouping affects the analysis results, so invalidate those too.
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, analysisId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, analysisId),
            }),
          ]);

          // Close modal and reset state
          handleCloseModal();
        },
        // TODO: Handle error states
        onError: (error) => {
          console.error('Error creating brand group:', error);
          // Optionally, show an error message to the user here (toast or in modal)
        },
      },
    });

  const { mutate: deleteGroup, isPending: isPendingDeleteGroup } =
    useDeleteBrandAssociationGroup({
      mutation: {
        onSuccess: async () => {
          await Promise.all([
            // Invalidate associated brands query to refresh the brand list
            queryClient.invalidateQueries({
              queryKey: getGetAssociatedBrandsQueryKey(brandId),
            }),
            // Grouping affects the analysis results, so invalidate those too.
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, analysisId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, analysisId),
            }),
          ]);
          // Close modal and reset state
          handleCloseModal();

          // Close confirm
          handleCloseUngroupConfirm();
        },
        onError: (error) => {
          console.error('Error deleting brand group:', error);
          // Optionally, show an error message to the user here (toast or in modal)
          window.alert(
            // TODO: improve this UX when we know more about why this might fail
            'An error occurred while ungrouping the brands. Please try again later.',
          );
        },
      },
    });

  // `groupToEdit.id` can be either a string (non-empty) or a number (not NaN).
  // This is required because the API may return either type depending on the backend implementation.
  // If the API is updated to always return a string or number, restrict this check accordingly.
  const isValidGroupId =
    (typeof groupToEdit?.id === 'string' && groupToEdit.id.length > 0) ||
    (typeof groupToEdit?.id === 'number' && !isNaN(groupToEdit.id));

  const handleSaveChanges = () => {
    if (!groupName.trim() || !isValidGroupId) return;

    createGroup({
      brandId,
      data: {
        groupName: groupName.trim(),
        brandIds: groupedBrandIds,
      },
    });
  };

  const handleCloseModal = () => {
    setGroupName('');
    setGroupedBrandIds([]);
    onClose();
  };

  // Get display names for the tags
  const getDisplayNames = () => {
    return groupedBrandIds
      .map((brandId) => {
        // First check within the group's brands
        if (groupToEdit && groupToEdit.type === 'group') {
          const groupBrand = groupToEdit.groupedBrands?.find(
            (b) => String(b.id) === brandId,
          );
          if (groupBrand) {
            return groupBrand.name || '';
          }
        }

        // Fallback to all brands
        const brand = allBrands.find((b) => String(b.id) === brandId);
        if (!brand) return '';
        return brand.type === 'group'
          ? `Group: ${brand.name}`
          : brand.name || '';
      })
      .filter(Boolean);
  };

  const handleUngroupBrands = () => {
    setIsUngroupConfirmOpen(true);
  };

  const handleCloseUngroupConfirm = () => setIsUngroupConfirmOpen(false);

  const handleConfirmUngroup = () => {
    if (!isValidGroupId) {
      console.error(
        `Invalid groupId provided for ungrouping. groupToEdit?.id: ${groupToEdit?.id} (type: ${typeof groupToEdit?.id})`,
      );
      // close the ungroup confirmation dialog
      handleCloseUngroupConfirm();
      return;
    }
    deleteGroup({ brandId, brandGroupId: groupToEdit.id });
  };

  if (!groupToEdit || groupToEdit.type !== 'group') return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={isUngroupConfirmOpen ? () => {} : handleCloseModal}
        as="div"
      >
        <Modal.Panel>
          <Modal.Container>
            <Modal.Container.Header className="yst-mb-4">
              <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                Edit brand group
              </Modal.Title>
            </Modal.Container.Header>
            <Modal.Container.Content className="yst-mb-8 yst-space-y-4">
              {/* Description */}
              <p className="yst-text-sm yst-text-slate-600">
                Update the group name or ungroup brands by removing their tags.
              </p>

              {/* Group Name Field */}
              <div>
                <label
                  htmlFor="editGroupName"
                  className="yst-mb-2 yst-block yst-text-sm yst-font-medium"
                >
                  Group name
                </label>
                <TextInput
                  id="editGroupName"
                  value={groupName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setGroupName(e.target.value)
                  }
                  placeholder="Enter group name"
                  className="yst-w-full"
                />
              </div>

              {/* Grouped Brands TagField */}
              <div>
                <TagField
                  readOnly
                  id="editGroupedBrands"
                  label="Grouped brands"
                  tags={getDisplayNames()}
                  onRemoveTag={(indexToRemove: number) => {
                    // prevent user from removing the last brand in the group
                    // by showing the ungroup brands confirmation modal.
                    if (groupedBrandIds.length < 2) {
                      return setIsUngroupConfirmOpen(true);
                    }

                    // Remove the brand at the given index from groupedBrandIds
                    setGroupedBrandIds((prev) =>
                      prev.filter((_, idx) => idx !== indexToRemove),
                    );
                  }}
                  onAddTag={() => {
                    // Not needed for this use case - brands are managed elsewhere
                  }}
                />
                {groupedBrandIds.length === 0 ? (
                  <p className="yst-mt-2 yst-text-sm">
                    Click "Ungroup brands" to remove this group.
                  </p>
                ) : (
                  <p className="yst-mt-2 yst-text-sm yst-italic yst-text-gray-500">
                    Note: To add brands to this group, save this group, then use
                    the checkboxes to select this group as well as any
                    additional brands to be grouped.
                  </p>
                )}
              </div>
            </Modal.Container.Content>

            <Modal.Container.Footer>
              <div className="yst-flex yst-w-full yst-items-center yst-justify-between">
                {/* Left: ungroup button aligned to start */}
                <div className="yst-flex yst-justify-start">
                  <Button
                    variant="tertiary"
                    onClick={handleUngroupBrands}
                    disabled={isPendingCreateGroup || !isValidGroupId}
                    className="yst-text-red-600"
                  >
                    Ungroup brands
                  </Button>
                </div>

                {/* Right: cancel and save buttons remain at the end */}
                <div className="yst-flex yst-justify-end yst-gap-2">
                  <Button variant="secondary" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveChanges}
                    disabled={!groupName.trim() || isPendingCreateGroup}
                    isLoading={isPendingCreateGroup}
                  >
                    {isPendingCreateGroup ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              </div>
            </Modal.Container.Footer>
          </Modal.Container>
        </Modal.Panel>
      </Modal>

      <DeleteConfirmationModal
        isOpen={isUngroupConfirmOpen}
        onClose={handleCloseUngroupConfirm}
        onConfirm={handleConfirmUngroup}
        disabled={!isValidGroupId || isPendingDeleteGroup}
        isLoading={isPendingDeleteGroup}
        title="Ungroup this brand group?"
        description={
          <>
            <p>
              The group will be removed, but the brands inside will remain in
              your brand list.
            </p>
            <p>
              This action cannot be undone. To create a new group with these
              brands, simply select them and create a new group.
            </p>
          </>
        }
        confirmText="Yes, ungroup"
      />
    </>
  );
};

export default EditGroupModal;
